/**
 * App-level error boundaries — two complementary surfaces, one shared brand panel.
 *
 *  - `RouteErrorComponent` is shaped for TanStack Router's `defaultErrorComponent`: it receives
 *    `{ error, reset }` (ErrorComponentProps) and renders the calm branded panel with a Try-again
 *    button wired to `reset` (which clears the router's caught error and re-renders the route).
 *  - `ErrorBoundary` is a React class component for the top-level, belt-and-braces wrap of the whole
 *    app root — it catches render errors that escape the router (provider trees, the router shell
 *    itself) where `reset` is a full reload.
 *
 * Both lean on the existing splash/login brand register (see styles/global.css + components/Login.tsx)
 * rather than introducing new CSS, and surface the message + stack only in DEV.
 */
import { Component, useEffect } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from '@salonomy/ui';
import { reportError } from '../lib/report.js';

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * The shared branded fallback. Calm copy, a single primary action, and — only under
 * `import.meta.env.DEV` — the error message and stack for the developer at the keyboard.
 */
function ErrorPanel({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="login" role="alert">
      <div className="login__card" style={{ textAlign: 'center' }}>
        <div className="login__mark">
          Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
        </div>
        <div className="login__sub">Something went wrong.</div>
        <p className="muted" style={{ margin: '0 0 24px', fontSize: 14, lineHeight: 1.6 }}>
          The console hit an unexpected snag. Your work is safe — let's try that again.
        </p>
        <Button size="lg" block onClick={onReset}>
          Try again
        </Button>
        {import.meta.env.DEV ? (
          <details
            className="mono"
            style={{
              marginTop: 20,
              textAlign: 'left',
              fontSize: 12,
              color: 'var(--slm-slate)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <summary style={{ cursor: 'pointer', marginBottom: 8 }}>
              {error.message || 'Error'}
            </summary>
            {error.stack ?? String(error)}
          </details>
        ) : null}
      </div>
    </div>
  );
}

/**
 * TanStack-Router-compatible error component. Pass as the router's `defaultErrorComponent`:
 *   createRouter({ ..., defaultErrorComponent: RouteErrorComponent })
 * `reset` clears the router's error state and retries the failed route.
 */
export function RouteErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  // WS14 — report the route error once on mount. reportError dedupes, so a re-render (or StrictMode's
  // double mount) won't double-send the same error; the effect is intentionally keyed on `error`.
  useEffect(() => {
    reportError(error, { kind: 'render' });
  }, [error]);
  return <ErrorPanel error={toError(error)} onReset={reset} />;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level class error boundary — the belt-and-braces wrap around the whole app root for render
 * errors that the router can't catch. Wrap <App/> (or the root element) with it:
 *   <ErrorBoundary><App /></ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: toError(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Surface to the console for the developer.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('App error boundary caught:', error, info.componentStack);
    }
    // WS14 — report render errors to the LOG-ONLY ingest in ALL envs (not just DEV). Fire-and-forget:
    // reportError never throws and dedupes, so a render loop / StrictMode double-mount can't spam.
    reportError(error, { kind: 'render', componentStack: info.componentStack ?? undefined });
  }

  private handleReset = (): void => {
    // At the app root there's no narrower state to recover — a full reload is the honest retry.
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return <ErrorPanel error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
